import { AttackStatus } from '../attack-status'
import { AgentLanguage } from '../agent-language'

export enum RuleName {
  CMD_INJECTION = 'cmd-injection',
  CMD_INJECTION_CHAINED_COMMANDS = 'cmd-injection-semantic-chained-commands',
  CMD_INJECTION_BACKDOORS = 'cmd-injection-command-backdoors',
  CMD_INJECTION_DANGEROUS_PATHS = 'cmd-injection-semantic-dangerous-paths',
  CMD_INJECTION_PROCESS_HARDENING = 'cmd-injection-process-hardening',
  PATH_TRAVERSAL = 'path-traversal',
  PATH_TRAVERSAL_FILE_SECURITY_BYPASS = 'path-traversal-semantic-file-security-bypass',
  SQL_INJECTION = 'sql-injection',
  SQL_INJECTION_CHAINED_QUERIES = 'sql-injection-semantic-chaining',
  SQL_INJECTION_TAUTOLOGIES = 'sql-injection-semantic-tautologies',
  SQL_INJECTION_SUSPICIOUS_UNIONS = 'sql-injection-semantic-suspicious-unions',
  SQL_INJECTION_DANGEROUS_FUNCTIONS = 'sql-injection-semantic-dangerous-functions',
  UNSAFE_FILE_UPLOAD = 'unsafe-file-upload',
  ZIP_FILE_OVERWRITE = 'zip-file-overwrite'
}

export const allRuleNames = Object.values(RuleName)

export type DetectionType = 'UNKNOWN' | 'SEMANTIC_ANALYSIS'
export const allDetectionTypes: DetectionType[] = [
  'SEMANTIC_ANALYSIS',
  'UNKNOWN'
]

export interface Application {
  readonly name: string
  readonly id: string
  readonly language: string
}

export interface Server {
  readonly name: string
  readonly id: string
}

export interface StackFrame {
  readonly type: string
  readonly description: string
}

export interface AttackEventDetails {
  findings?: string[]
  query?: string
  command?: string
  entryName?: string
  path?: string
  start?: number
  end?: number
  commandArgs?: string[]
  directory?: string
}

export const validAttackEventDetailsKeys = [
  'path',
  'command',
  'query',
  'findings',
  'entryName',
  'start',
  'end',
  'commandArgs',
  'directory'
]

export interface AttackEvent {
  readonly id: string
  readonly sourceIP?: string
  readonly result: AttackStatus
  readonly ruleUuid: string
  readonly application: Application
  readonly server: Server
  readonly userInput?: UserInput
  readonly details?: AttackEventDetails
  readonly detectionType?: string
  readonly httpRequest?: HttpRequest
  readonly callLocation?: string
  readonly file?: string
  readonly method?: string
  readonly line?: number
  readonly stackFrames?: StackFrame[]
  readonly lastCustomFrame?: number
  readonly signature?: string
  readonly vector?: string
}

export interface UserInput {
  readonly value: string
  readonly name?: string
  readonly type?: UserInputType
  readonly truncatedValue?: boolean
}

export type UserInputType =
  | 'PARAMETER_NAME'
  | 'PARAMETER_VALUE'
  | 'HEADER'
  | 'QUERYSTRING'
  | 'BODY'
  | 'COOKIE_NAME'
  | 'COOKIE_VALUE'
  | 'URI'
  | 'SOCKET'
  | 'JSON_VALUE'
  | 'JSON_ARRAYED_VALUE'
  | 'MULTIPART_CONTENT_TYPE'
  | 'MULTIPART_VALUE'
  | 'MULTIPART_FIELD_NAME'
  | 'MULTIPART_NAME'
  | 'XML_VALUE'
  | 'DWR_VALUE'
  | 'UNKNOWN'
  | 'METHOD'
  | 'REQUEST'

export interface HttpRequest {
  readonly uri: string
  readonly method: string
  readonly queryString?: string | null
  readonly version: string
  readonly body?: string
  readonly headers: string
  readonly parameters: string
}

interface AttackEventRuleChildProps {
  readonly renderOverview: (props: {
    readonly httpRequest?: HttpRequest
    readonly userInput?: UserInput
    readonly application: Application
    readonly server: Server
    readonly result: AttackStatus
    readonly sourceIP?: string
    readonly organizationID: string
    readonly details?: string
    readonly callLocation?: string
  }) => React.ReactNode
}

export interface AttackEventRule {
  readonly children: (props: AttackEventRuleChildProps) => React.ReactNode
}

export function getUserInputValue(userInput: UserInput | undefined) {
  return (userInput && userInput.value) || ''
}

export const testAttackEvents = [
  {
    id: '90081143-b76b-4808-a7bc-de00b97a9376',
    ruleUuid: 'reflected-xss',
    result: AttackStatus.EXPLOITED,
    sourceIP: '106.161.56.215',
    received: '2020-01-08T15:53:03.000Z',
    displayName: 'Cross-Site Scripting',
    type: 'PR',
    url: '/WebGoat/attack',
    attackType: 'MANUAL',
    attackers: ['guest'],
    sourceName: undefined,
    tags: [],
    details: {
      findings: undefined,
      query: undefined,
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: {
      value: '" onmouseover="alert(hnzrs460)',
      truncatedValue: false,
      name: 'field1',
      type: 'PARAMETER_VALUE' as UserInputType
    },
    application: {
      id: '2f84d1d5-f0eb-45e7-8f74-3344322c1eda',
      name: 'APP_WebGoat_2020_0',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '1',
      name: 'SERVER_WebGoat_2020_0',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'UNKNOWN',
    httpRequest: {
      method: 'POST',
      queryString: 'Screen=1574219258&menu=1700',
      uri: '/WebGoat/attack',
      version: '1.0',
      body: 'field1=%22+onmouseover%3D%22alert%28hnzrs460%29&amp;field2=oxkvs123&amp;field3=bcryl217&amp;field4=qixkr518&amp;field5=zzgbu507&amp;field6=%22%27+%26+ping+192.168.0.1&amp;field7=witjz631&amp;=gqtap601',
      headers:
        '{"content-length":["172"],"cookie":["JSESSIONID\\u003dED47D8BEC4F4C88E3D54A07E7D5566A4"],"host":["localhost:8082"],"User-Agent":["Apache-HttpClient/4.5.2 (Java/1.8.0_211)"],"content-type":["application/x-www-form-urlencoded; charset\\u003dUTF-8"],"connection":["Keep-Alive"],"accept-encoding":["gzip,deflate"]}',
      parameters:
        '{"field1":["\\" onmouseover\\u003d\\"alert(hnzrs460)"],"Screen":["1574219258"],"field7":["witjz631"],"field6":["\\"\\u0027 \\u0026 ping 192.168.0.1"],"menu":["1700"],"field3":["bcryl217"],"field2":["oxkvs123"],"field5":["zzgbu507"],"field4":["qixkr518"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed the following suspicious value enter the application through the HTTP Request Parameter "{{source}}": {{/paragraph}}',
        introVariables: JSON.parse('{"source":"field1"}'),
        bodyTemplate:
          'POST /WebGoat/attack?Screen=1574219258&menu=1700 HTTP/1.0\n{{{nl}}}\n{{{nl}}}field1={{#taint}}%22+onmouseover%3D%22alert%28hnzrs460%29{{/taint}}&field2=oxkvs123&field3=bcryl217&field4=qixkr518&field5=zzgbu507&field6=%22%27+%26+ping+192.168.0.1&field7=witjz631',
        bodyVariables: undefined
      },
      {
        type: 'dataflow',
        introTemplate:
          '{{#paragraph}}This value was again observed being put into the HTTP response at org.springframework.web.filter.DelegatingFilterProxy.invokeDelegate(DelegatingFilterProxy.java:343):{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          '\n{{{nl}}}\n{{{nl}}}\n{{{nl}}}\n{{{nl}}}\n{{{nl}}}&lt;!--&nbsp;HTML&nbsp;fragment&nbsp;correpsonding&nbsp;to&nbsp;the&nbsp;lesson&nbsp;content&nbsp;--&gt;\n{{{nl}}}\n{{{nl}}}\n{{{nl}}}&lt;div&nbsp;id=&quot;lessonContent&quot;&gt;\n{{{nl}}}&nbsp;&nbsp;&nbsp;&nbsp;\n{{{nl}}}&nbsp;&nbsp;&nbsp;&nbsp;This&nbsp;website&nbsp;performs&nbsp;both&nbsp;client&nbsp;and&nbsp;server&nbsp;side&nbsp;validation.&nbsp;&nbsp;For&nbsp;this&nbsp;exercise,&nbsp;your&nbsp;job&nbsp;is&nbsp;to&nbsp;break&nbsp;the&nbsp;client&nbsp;side&nbsp;validation&nbsp;and&nbsp;send&nbsp;the&nbsp;\n{{{nl}}}&nbsp;website&nbsp;input&nbsp;that&nbsp;it&nbsp;wasn&#39;t&nbsp;expecting.&nbsp;&lt;b&gt;&nbsp;You&nbsp;must&nbsp;break&nbsp;all&nbsp;7&nbsp;validators&nbsp;at&nbsp;the&nbsp;same&nbsp;time.&nbsp;&lt;/b&gt;\n{{{nl}}}&lt;/div&gt;\n{{{nl}}}&lt;div&nbsp;id=&quot;message&quot;&nbsp;class=&quot;info&quot;&gt;&lt;BR&gt;&nbsp;*&nbsp;&lt;BR&gt;Server&nbsp;side&nbsp;validation&nbsp;violation:&nbsp;&nbsp;You&nbsp;succeeded&nbsp;for&nbsp;Field1.&lt;BR&gt;Server&nbsp;side&nbsp;validation&nbsp;violation:&nbsp;&nbsp;You&nbsp;succeeded&nbsp;for&nbsp;Field2.&lt;BR&gt;Server&nbsp;side&nbsp;validation&nbsp;violation:&nbsp;&nbsp;You&nbsp;succeeded&nbsp;forField4.&lt;BR&gt;Server&nbsp;side&nbsp;validation&nbsp;violation:&nbsp;&nbsp;You&nbsp;succeeded&nbsp;forField5.&lt;BR&gt;Server&nbsp;side&nbsp;validation&nbsp;violation:&nbsp;&nbsp;You&nbsp;succeeded&nbsp;forField6.&lt;BR&gt;Server&nbsp;side&nbsp;validation&nbsp;violation:&nbsp;&nbsp;You&nbsp;succeeded&nbsp;forField7.&lt;/div&gt;\n{{{nl}}}\n{{{nl}}}\n{{{nl}}}&lt;div&nbsp;id=&quot;lessonContent&quot;&gt;&lt;form&nbsp;accept-charset=&#39;UNKNOWN&#39;&nbsp;method=&#39;POST&#39;&nbsp;name=&#39;form&#39;&nbsp;action=&#39;#attack/1574219258/1700&#39;&nbsp;enctype=&#39;&#39;&gt;&lt;SCRIPT&gt;\n{{{nl}}}regex1=/^[a-z]{3}$/;\n{{{nl}}}regex2=/^[0-9]{3}$/;\n{{{nl}}}regex3=/^[a-zA-Z0-9&nbsp;]*$/;\n{{{nl}}}regex4=/^(one|two|three|four|five|six|seven|eight|nine)$/;\n{{{nl}}}regex5=/^\\d{5}$/;\n{{{nl}}}regex6=/^\\d{5}(-\\d{4})?$/;\n{{{nl}}}regex7=/^[2-9]\\d{2}-?\\d{3}-?\\d{4}$/;\n{{{nl}}}function&nbsp;validate()&nbsp;{&nbsp;\n{{{nl}}}msg=&#39;JavaScript&nbsp;found&nbsp;form&nbsp;errors&#39;;&nbsp;err=0;&nbsp;\n{{{nl}}}if&nbsp;(!regex1.test(document.form.field1.value))&nbsp;{err+=1;&nbsp;msg+=&#39;\\n&nbsp;&nbsp;bad&nbsp;field1&#39;;}\n{{{nl}}}if&nbsp;(!regex2.test(document.form.field2.value))&nbsp;{err+=1;&nbsp;msg+=&#39;\\n&nbsp;&nbsp;bad&nbsp;field2&#39;;}\n{{{nl}}}if&nbsp;(!regex3.test(document.form.field3.value))&nbsp;{err+=1;&nbsp;msg+=&#39;\\n&nbsp;&nbsp;bad&nbsp;field3&#39;;}\n{{{nl}}}if&nbsp;(!regex4.test(document.form.field4.value))&nbsp;{err+=1;&nbsp;msg+=&#39;\\n&nbsp;&nbsp;bad&nbsp;field4&#39;;}\n{{{nl}}}if&nbsp;(!regex5.test(document.form.field5.value))&nbsp;{err+=1;&nbsp;msg+=&#39;\\n&nbsp;&nbsp;bad&nbsp;field5&#39;;}\n{{{nl}}}if&nbsp;(!regex6.test(document.form.field6.value))&nbsp;{err+=1;&nbsp;msg+=&#39;\\n&nbsp;&nbsp;bad&nbsp;field6&#39;;}\n{{{nl}}}if&nbsp;(!regex7.test(document.form.field7.value))&nbsp;{err+=1;&nbsp;msg+=&#39;\\n&nbsp;&nbsp;bad&nbsp;field7&#39;;}\n{{{nl}}}if&nbsp;(&nbsp;err&nbsp;&gt;&nbsp;0&nbsp;)&nbsp;alert(msg);\n{{{nl}}}else&nbsp;document.form.submit();\n{{{nl}}}}&nbsp;\n{{{nl}}}&lt;/SCRIPT&gt;\n{{{nl}}}&lt;div&gt;Field1:&nbsp;exactly&nbsp;three&nbsp;lowercase&nbsp;characters(^[a-z]{3}$)&lt;/div&gt;&lt;div&gt;&lt;textarea&nbsp;cols=&#39;25&#39;&nbsp;name=&#39;field1&#39;&nbsp;rows=&#39;1&#39;&gt;{{#taint}}&quot;&nbsp;onmouseover=&quot;alert(hnzrs460){{/taint}}',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate:
          '{{#paragraph}}We did not block this attack because blocking was not enabled for {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/applications/2f84d1d5-f0eb-45e7-8f74-3344322c1eda$$LINK_DELIM$$APP_WebGoat_2020_0{{/link}} on {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/servers/1/overview$$LINK_DELIM$$SERVER_WebGoat_2020_0{{/link}}.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: 'a3279e00-6ef9-4b63-9639-9302215c91d9',
    ruleUuid: 'sql-injection',
    result: AttackStatus.EXPLOITED,
    sourceIP: '106.161.56.215',
    received: '2020-01-08T15:52:24.000Z',
    displayName: 'SQL Injection',
    type: 'PR',
    url: '/WebGoat/attack',
    attackType: 'MANUAL',
    attackers: ['guest'],
    sourceName: undefined,
    tags: [],
    details: {
      findings: undefined,
      query:
        "SELECT * FROM user_system_data WHERE user_name = 'nbbul387' and password = '\"' & ping 192.168.0.1'",
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: {
      value: '"\' & ping 192.168.0.1',
      truncatedValue: false,
      name: 'Password',
      type: 'PARAMETER_VALUE' as UserInputType
    },
    application: {
      id: '2f84d1d5-f0eb-45e7-8f74-3344322c1eda',
      name: 'APP_WebGoat_2020_0',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '1',
      name: 'SERVER_WebGoat_2020_0',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'INPUT_TRACING',
    httpRequest: {
      method: 'POST',
      queryString: 'Screen=1036971378&menu=1200',
      uri: '/WebGoat/attack',
      version: '1.0',
      body: 'Username=nbbul387&amp;Password=%22%27+%26+ping+192.168.0.1&amp;SUBMIT=apdwk308',
      headers:
        '{"content-length":["70"],"cookie":["JSESSIONID\\u003dED47D8BEC4F4C88E3D54A07E7D5566A4"],"host":["localhost:8082"],"User-Agent":["Apache-HttpClient/4.5.2 (Java/1.8.0_211)"],"content-type":["application/x-www-form-urlencoded; charset\\u003dUTF-8"],"connection":["Keep-Alive"],"accept-encoding":["gzip,deflate"]}',
      parameters:
        '{"menu":["1200"],"Username":["nbbul387"],"Screen":["1036971378"],"SUBMIT":["apdwk308"],"Password":["contrast-redacted-authentication-info"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed the following suspicious value enter the application through the HTTP Request Parameter "{{source}}": {{/paragraph}}',
        introVariables: JSON.parse('{"source":"Password"}'),
        bodyTemplate:
          'POST /WebGoat/attack?Screen=1036971378&menu=1200 HTTP/1.0\n{{{nl}}}\n{{{nl}}}Password={{#redacted}}contrast-redacted-authentication-info{{/redacted}}&SUBMIT=apdwk308&Username=nbbul387',
        bodyVariables: undefined
      },
      {
        type: 'dataflow',
        introTemplate:
          '{{#paragraph}}This value was again observed altering the meaning of the SQL query executed within org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108):{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;nbbul387&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;{{#taint}}&quot;&#39;&nbsp;&amp;&nbsp;ping&nbsp;192.168.0.1{{/taint}}&#39;',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate:
          '{{#paragraph}}We did not block this attack because blocking was not enabled for {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/applications/2f84d1d5-f0eb-45e7-8f74-3344322c1eda$$LINK_DELIM$$APP_WebGoat_2020_0{{/link}} on {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/servers/1/overview$$LINK_DELIM$$SERVER_WebGoat_2020_0{{/link}}.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        introText: 'intro',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: 'd9b50f44-2583-4f94-8a75-938bff2e5097',
    ruleUuid: 'sql-injection',
    result: AttackStatus.EXPLOITED,
    sourceIP: undefined,
    received: '2020-01-08T18:48:15.000Z',
    displayName: 'SQL Injection',
    type: 'PR',
    url: undefined,
    attackType: 'MANUAL',
    attackers: [],
    sourceName: undefined,
    tags: [],
    details: {
      findings: [
        'CHAINING',
        'SUSPICIOUS_UNIONS',
        'DANGEROUS_FUNCTIONS',
        'TAUTOLOGIES'
      ],
      query:
        "SELECT * FROM user_system_data WHERE user_name = '' or 112=112--' and password = '' or 1+2=3 --'SELECT * FROM user_system_data WHERE user_name = '' or 112=112--' and password = '' or 1+2=3 --'",
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: {
      value:
        "SELECT * FROM user_system_data WHERE user_name = '' or 112=112--' and password = '' or 1+2=3 --'SELECT * FROM user_system_data WHERE user_name = '' or 112=112--' and password = '' or 1+2=3 --'",
      truncatedValue: false,
      name: undefined,
      type: 'UNKNOWN' as UserInputType
    },
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'SEMANTIC_ANALYSIS',
    httpRequest: undefined,
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed the following suspicious value enter the application through the Unknown: {{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      },
      {
        type: 'dataflow',
        introTemplate:
          '{{#paragraph}}This value was again observed altering the meaning of the SQL query executed within org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108):{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          '{{#taint}}{{/taint}}SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;112=112--&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;112=112--&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate:
          '{{#paragraph}}We did not block this attack because blocking was not enabled for {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/applications/965faeda-d021-4b60-81b4-9d436e7d6a0c$$LINK_DELIM$$AgentMessageGeneratorJava{{/link}} on {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/servers/2/overview$$LINK_DELIM$$FakeServer{{/link}}.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: '40f69cb7-149b-45ff-85e8-e2e0252e9e41',
    ruleUuid: 'bot-blocker',
    result: AttackStatus.BLOCKED,
    sourceIP: '0:0:0:0:0:0:0:1',
    received: '2019-04-04T14:23:54.000Z',
    displayName: 'Bot Blocker',
    type: 'BB',
    url: '/get-toggle',
    attackType: 'MANUAL',
    attackers: [],
    sourceName: undefined,
    tags: [],
    details: {
      findings: undefined,
      query: undefined,
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: {
      value: 'some user agent',
      truncatedValue: false,
      name: undefined,
      type: undefined
    },
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: 'JAVA'
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: undefined,
    httpRequest: {
      method: 'GET',
      queryString: undefined,
      uri: '/get-toggle',
      version: '1.0',
      body: undefined,
      headers:
        '{"content-length":["26"],"accept-language":["en-US,en;q\\u003d0.9"],"cookie":["_ga\\u003dGA1.1.1072054443.1519245824; __utmz\\u003d111872281.1538493544.1.1.utmcsr\\u003d(direct)|utmccn\\u003d(direct)|utmcmd\\u003d(none); __utma\\u003d111872281.1072054443.1519245824.1551810215.1551819849.75; _gid\\u003dGA1.1.1803574245.1554125944; BAYEUX_BROWSER\\u003d2q9ro0s910q71k2e; JSESSIONID\\u003dA24D626415E341EEA40446366089D644"],"origin":["null"],"host":["localhost:8090"],"upgrade-insecure-requests":["1"],"User-Agent":["Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/73.0.3683.86 Safari/537.36"],"connection":["keep-alive"],"content-type":["application/x-www-form-urlencoded"],"cache-control":["max-age\\u003d0"],"accept-encoding":["gzip, deflate, br"],"accept":["text/html,application/xhtml+xml,application/xml;q\\u003d0.9,image/webp,image/apng,*/*;q\\u003d0.8,application/signed-exchange;v\\u003db3"]}',
      parameters: '{"funds":["100000"],"Change":["Toggle"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed a request from some bot, who is on our bad boy bot blacklist. We blocked that beast.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: '96640ac1-aa07-4bf6-885f-b09bf7820064',
    ruleUuid: 'path-traversal',
    result: AttackStatus.EXPLOITED,
    sourceIP: undefined,
    received: '2020-01-08T19:04:06.000Z',
    displayName: 'Path Traversal',
    type: 'PR',
    url: undefined,
    attackType: 'MANUAL',
    attackers: [],
    sourceName: undefined,
    tags: [],
    details: {
      findings: ['CUSTOM_CODE_ACCESSING_SYSTEM_FILES'],
      query: undefined,
      command: undefined,
      entryName: undefined,
      path: 'ls -l ./../../../../'
    },
    userInput: {
      value: '../../../../../../../../../etc/passwd',
      truncatedValue: false,
      name: undefined,
      type: 'UNKNOWN' as UserInputType
    },
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'SEMANTIC_ANALYSIS',
    httpRequest: undefined,
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed the following suspicious value enter the application through the Unknown: {{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      },
      {
        type: 'dataflow',
        introTemplate:
          '{{#paragraph}}This value was then observed in a file opening call within &lt;anonymous&gt;(/Users/adamstone/Documents/NodeTestBench/vulnerabilities/command_injection/index.js:23):{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: '{{#taint}}ls -l ./../../../../{{/taint}}',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate:
          '{{#paragraph}}We did not block this attack because blocking was not enabled for {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/applications/965faeda-d021-4b60-81b4-9d436e7d6a0c$$LINK_DELIM$$AgentMessageGeneratorJava{{/link}} on {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/servers/2/overview$$LINK_DELIM$$FakeServer{{/link}}.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: 'd67ad11c-99f9-484c-ab79-a947e6a174d1',
    ruleUuid: 'VP: 67bc19e6-61c9-4aac-a48a-244179b01ce0',
    result: AttackStatus.BLOCKED,
    sourceIP: '0:0:0:0:0:0:0:1',
    received: '2019-04-04T14:23:54.000Z',
    displayName: 'VP: 67bc19e6-61c9-4aac-a48a-244179b01ce0',
    type: 'VP',
    url: '/get-toggle',
    attackType: 'MANUAL',
    attackers: [],
    sourceName: undefined,
    tags: [],
    details: {
      findings: undefined,
      query: undefined,
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: undefined,
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: undefined,
    httpRequest: {
      method: 'GET',
      queryString: undefined,
      uri: '/get-toggle',
      version: '1.0',
      body: undefined,
      headers:
        '{"content-length":["26"],"accept-language":["en-US,en;q\\u003d0.9"],"cookie":["_ga\\u003dGA1.1.1072054443.1519245824; __utmz\\u003d111872281.1538493544.1.1.utmcsr\\u003d(direct)|utmccn\\u003d(direct)|utmcmd\\u003d(none); __utma\\u003d111872281.1072054443.1519245824.1551810215.1551819849.75; _gid\\u003dGA1.1.1803574245.1554125944; BAYEUX_BROWSER\\u003d2q9ro0s910q71k2e; JSESSIONID\\u003dA24D626415E341EEA40446366089D644"],"origin":["null"],"host":["localhost:8090"],"upgrade-insecure-requests":["1"],"User-Agent":["Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/73.0.3683.86 Safari/537.36"],"connection":["keep-alive"],"content-type":["application/x-www-form-urlencoded"],"cache-control":["max-age\\u003d0"],"accept-encoding":["gzip, deflate, br"],"accept":["text/html,application/xhtml+xml,application/xml;q\\u003d0.9,image/webp,image/apng,*/*;q\\u003d0.8,application/signed-exchange;v\\u003db3"]}',
      parameters: '{"funds":["100000"],"Change":["Toggle"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed the following request which matched the criteria defined in the triggered virtual patch rule: {{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: 'GET /get-toggle HTTP/1.0\n{{{nl}}}\n{{{nl}}}',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate: '{{#paragraph}}We blocked this attack.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: '',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: '76134e0b-8bd4-4a21-b88d-27b23e0526af',
    ruleUuid: 'unsafe-file-upload',
    result: AttackStatus.BLOCKED_PERIMETER,
    sourceIP: '127.0.0.1',
    received: '2020-01-08T19:15:35.000Z',
    displayName: 'Unsafe File Upload',
    type: 'PR',
    url: '/WebGoat/attack',
    attackType: 'MANUAL',
    attackers: ['webgoat'],
    sourceName: undefined,
    tags: [],
    details: undefined,
    userInput: {
      value: 'shell.avi.php1g',
      truncatedValue: false,
      name: 'filename',
      type: 'MULTIPART_NAME' as UserInputType
    },
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'UNKNOWN',
    httpRequest: {
      method: 'POST',
      queryString: 'Screen=2027530490&menu=1600',
      uri: '/WebGoat/attack',
      version: '1.0',
      body: undefined,
      headers:
        '{"referer":["http://localhost.:8080/WebGoat/start.mvc"],"content-length":["133932"],"accept-language":["en-US,en;q\\u003d0.5"],"cookie":["JSESSIONID\\u003dE695DF0D9AA2036B6E5A63A2EF463DCE"],"host":["localhost.:8080"],"User-Agent":["Mozilla/5.0 (Macintosh; Intel Mac OS X 10.14; rv:67.0) Gecko/20100101 Firefox/67.0"],"x-requested-with":["XMLHttpRequest"],"content-type":["multipart/form-data; boundary\\u003d---------------------------1271476297923523821683291140"],"connection":["close"],"dnt":["1"],"accept-encoding":["gzip,deflate"],"accept":["*/*"]}',
      parameters: '{"Screen":["2027530490"],"menu":["1600"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}nullMultipart File Name "{{source}}": {{/paragraph}}',
        introVariables: JSON.parse('{"source":"filename"}'),
        bodyTemplate:
          'POST /WebGoat/attack?Screen=2027530490&amp;menu=1600 HTTP/1.0\n{{{nl}}}\n{{{nl}}}{{#taint}}filename{{/taint}}',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate: '{{#paragraph}}We blocked this attack.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: '',
    lastCustomFrame: -1,
    signature: '',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: 'f34760f7-c646-4ed3-8530-a11d08c92078',
    ruleUuid: 'redos',
    result: AttackStatus.SUSPICIOUS,
    sourceIP: '104.253.78.198',
    received: '2020-01-08T19:35:52.000Z',
    displayName: 'Regular Expression DoS',
    type: 'PR',
    url: '/WebGoat/attack',
    attackType: 'MANUAL',
    attackers: ['guest'],
    sourceName: undefined,
    tags: [],
    details: {
      findings: undefined,
      query: undefined,
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: {
      value: "' or 112=112--",
      truncatedValue: false,
      name: 'SUBMIT',
      type: 'PARAMETER_VALUE' as UserInputType
    },
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'UNKNOWN',
    httpRequest: {
      method: 'POST',
      queryString: 'Screen=1135&menu=1200',
      uri: '/WebGoat/attack',
      version: '1.0',
      body: 'Username=%27+or+112%3D112--&amp;Password=%27+or+1%2B2%3D3+--&amp;SUBMIT=%27+or+112%3D112--',
      headers:
        '{"content-length":["82"],"cookie":["JSESSIONID\\u003d798C9EF64D586047B895658733214D40"],"host":["localhost:8080"],"User-Agent":["Apache-HttpClient/4.5.2 (Java/1.8.0_161)"],"content-type":["application/x-www-form-urlencoded; charset\\u003dUTF-8"],"connection":["Keep-Alive"],"accept-encoding":["gzip,deflate"]}',
      parameters:
        '{"Username":["\\u0027 or 112\\u003d112--"],"Screen":["1135"],"SUBMIT":["\\u0027 or 112\\u003d112--"],"menu":["1200"],"Password":["\\u0027 or 1+2\\u003d3 --"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed a probable RegexDoS attack in HTTP Request Parameter "{{source}}": {{/paragraph}}',
        introVariables: JSON.parse('{"source":"SUBMIT"}'),
        bodyTemplate:
          'POST /WebGoat/attack?Screen=1135&menu=1200 HTTP/1.0\n{{{nl}}}\n{{{nl}}}Password=%27+or+1%2B2%3D3+--&SUBMIT={{#taint}}%27+or+112%3D112--{{/taint}}&Username={{#taint}}%27+or+112%3D112--{{/taint}}',
        bodyVariables: undefined
      },
      {
        type: 'dataflow',
        introTemplate:
          '{{#paragraph}}Over 123123 milliseconds, 100 operations have occurred while evaluating the regular expression "(a+)+" on the input string:{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: 'aaaaaaaaaaaaaaa!',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate:
          '{{#paragraph}}We did not block this attack because blocking was not enabled for {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/applications/965faeda-d021-4b60-81b4-9d436e7d6a0c$$LINK_DELIM$$AgentMessageGeneratorJava{{/link}} on {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/servers/2/overview$$LINK_DELIM$$FakeServer{{/link}}.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: '1a514af2-4b3e-4fbd-af57-0acacb51afdf',
    ruleUuid: 'padding-oracle',
    result: AttackStatus.BLOCKED,
    sourceIP: '172.17.0.1',
    received: '2020-01-08T19:35:34.000Z',
    displayName: 'Padding Oracle',
    type: 'PR',
    url: '/oracle',
    attackType: 'MANUAL',
    attackers: [],
    sourceName: undefined,
    tags: [],
    details: {
      findings: undefined,
      query: undefined,
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: {
      value: '',
      truncatedValue: false,
      name: undefined,
      type: 'UNKNOWN' as UserInputType
    },
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'UNKNOWN',
    httpRequest: {
      method: 'POST',
      queryString: undefined,
      uri: '/oracle',
      version: '1.1',
      body: undefined,
      headers:
        '{"content-type":["application/octet-stream"],"connection":["Keep-Alive"],"host":["localhost:32812"],"content-length":["32"],"contrast-mq-name":["queue-000-PaddingOracleIT.agent_blocks_padding_oracle_attacks-contrast/padding-oracle:ibm-1.7-rev3"],"user-agent":["okhttp/3.9.1"],"accept-encoding":["gzip"]}',
      parameters: '{}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed a series of unexpected cryptographic exceptions when processing the following URL: {{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: 'POST /oracle HTTP/1.1\n{{{nl}}}\n{{{nl}}}',
        bodyVariables: undefined
      },
      {
        type: 'dataflow',
        introTemplate:
          '{{#paragraph}} Cryptographic algorithms sometimes "pad" their encrypted output, called ciphertext, so that they can be delivered in predictable sizes. The code at com.contrastsecurity.test.paddingoracle.AES.decrypt(AES.java:91) processed a ciphertext that threw a significant number of bad padding exceptions.\n{{{nl}}}\n{{{nl}}}This attack is called a padding oracle attack. When variable-length plaintext is used for encryption, padding has to be used to match the underlying cryptographic algorithm. Padding validation is then used to decrypt the ciphertext, which is the hallmark of an extremely effective attack whereby an attacker can remotely decipher the plaintext of an encrypted message.\n{{{nl}}}\n{{{nl}}}Here is the exception message:{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          '{{#taint}}Given&nbsp;final&nbsp;block&nbsp;not&nbsp;properly&nbsp;padded{{/taint}}',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate: '{{#paragraph}}We blocked this attack.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: 'b34ab640-a37f-4da6-954a-d976590f573c',
    ruleUuid: 'cmd-injection',
    result: AttackStatus.PROBED,
    sourceIP: '74.221.2.67',
    received: '2020-01-08T15:53:08.000Z',
    displayName: 'Command Injection',
    type: 'PR',
    url: '/WebGoat/attack',
    attackType: 'MANUAL',
    attackers: ['guest'],
    sourceName: undefined,
    tags: [],
    details: undefined,
    userInput: {
      value: '"\' & ping 192.168.0.1',
      truncatedValue: false,
      name: 'radio',
      type: 'PARAMETER_VALUE' as UserInputType
    },
    application: {
      id: '2f84d1d5-f0eb-45e7-8f74-3344322c1eda',
      name: 'APP_WebGoat_2020_0',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '1',
      name: 'SERVER_WebGoat_2020_0',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'UNKNOWN',
    httpRequest: {
      method: 'POST',
      queryString: 'Screen=82558034&menu=1700',
      uri: '/WebGoat/attack',
      version: '1.0',
      body: 'radio=%22%27+%26+ping+192.168.0.1&amp;radio=rqeag902&amp;shortinput=..%2F..%2F..%2F..%2F..%2Ffoo.bar%2500&amp;disabledinput=cvmtn299&amp;SUBMIT=piuaq482',
      headers:
        '{"content-length":["136"],"cookie":["JSESSIONID\\u003dE597ECA98C09B9EBB6718CA65B1A1698"],"host":["localhost:8082"],"User-Agent":["Apache-HttpClient/4.5.2 (Java/1.8.0_211)"],"content-type":["application/x-www-form-urlencoded; charset\\u003dUTF-8"],"connection":["Keep-Alive"],"accept-encoding":["gzip,deflate"]}',
      parameters:
        '{"Screen":["82558034"],"SUBMIT":["piuaq482"],"menu":["1700"],"disabledinput":["cvmtn299"],"radio":["\\"\\u0027 \\u0026 ping 192.168.0.1","rqeag902"],"shortinput":["../../../../../foo.bar%00"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed the following suspicious value enter the application through the HTTP Request Parameter "{{source}}": {{/paragraph}}',
        introVariables: JSON.parse('{"source":"radio"}'),
        bodyTemplate:
          'POST /WebGoat/attack?Screen=82558034&menu=1700 HTTP/1.0\n{{{nl}}}\n{{{nl}}}SUBMIT=piuaq482&disabledinput=cvmtn299&radio={{#taint}}%22%27+%26+ping+192.168.0.1{{/taint}}&radio={{#taint}}%22%27+%26+ping+192.168.0.1{{/taint}}radio=rqeag902&shortinput=..%2F..%2F..%2F..%2F..%2Ffoo.bar%2500',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate:
          '{{#paragraph}}This attempted attack did not require specific blocking because it did not cause any adverse actions within the application.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: 'e0fd6216-e3df-474e-8687-577095a0aee9',
    ruleUuid: 'xxe',
    result: AttackStatus.EXPLOITED,
    sourceIP: '0.0.0.0',
    received: '2020-01-09T16:24:54.000Z',
    displayName: 'XML External Entity Processing',
    type: 'PR',
    url: '/fake/xxe',
    attackType: 'MANUAL',
    attackers: [],
    sourceName: undefined,
    tags: [],
    details: {
      findings: undefined,
      query: undefined,
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: {
      value:
        '<?xml version="1.0"?><!DOCTYPE foo [<!ENTITY xxe SYSTEM "file:///etc/passwd">]><book><title>&xxe;</title></book><!DOCTYPE foo [<!ENTITY xxe SYSTEM "file:///etc/passwd">]><book><title>&xxe;</title></book>',
      truncatedValue: false,
      name: 'XML Prolog',
      type: 'UNKNOWN' as UserInputType
    },
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'UNKNOWN',
    httpRequest: {
      method: 'POST',
      queryString: '',
      uri: '/fake/xxe',
      version: '1.0',
      body: '<?xml version="1.0"?><!DOCTYPE foo [<!ENTITY xxe SYSTEM "file:///etc/passwd">]><book><title>&xxe;</title></book><!DOCTYPE foo [<!ENTITY xxe SYSTEM "file:///etc/passwd">]><book><title>&xxe;</title></book>',
      headers:
        '{"content-length":["82"],"cookie":[],"host":["localhost:8080"],"User-Agent":["Apache-HttpClient/4.5.2 (Java/1.8.0_161)"],"content-type":["application/xml; charset\\u003dUTF-8"],"connection":["Keep-Alive"],"accept-encoding":["gzip,deflate"]}',
      parameters: '{}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}While servicing this HTTP request:{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          'POST /fake/xxe HTTP/1.0\n{{{nl}}}\n{{{nl}}}\n\n{{{nl}}}{{{nl}}}&lt;?xml&nbsp;version=&quot;1.0&quot;?&gt;&lt;!DOCTYPE&nbsp;foo&nbsp;[&lt;!ENTITY&nbsp;xxe&nbsp;SYSTEM&nbsp;&quot;file:///etc/passwd&quot;&gt;]&gt;&lt;book&gt;&lt;title&gt;&amp;xxe;&lt;/title&gt;&lt;/book&gt;&lt;!DOCTYPE&nbsp;foo&nbsp;[&lt;!ENTITY&nbsp;xxe&nbsp;SYSTEM&nbsp;&quot;file:///etc/passwd&quot;&gt;]&gt;&lt;book&gt;&lt;title&gt;&amp;xxe;&lt;/title&gt;&lt;/book&gt;',
        bodyVariables: undefined
      },
      {
        type: 'document',
        introTemplate:
          '{{#paragraph}}We observed an XML external entity being declared in the prolog of the following XML document:{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          '&lt;?xml&nbsp;version=&quot;1.0&quot;?&gt;&lt;!DOCTYPE&nbsp;foo&nbsp;[&lt;!ENTITY&nbsp;xxe&nbsp;SYSTEM&nbsp;&quot;file:///etc/passwd&quot;&gt;]&gt;&lt;book&gt;&lt;title&gt;&amp;xxe;&lt;/title&gt;&lt;/book&gt;&lt;!DOCTYPE&nbsp;foo&nbsp;[&lt;!ENTITY&nbsp;xxe&nbsp;SYSTEM&nbsp;&quot;file:///etc/passwd&quot;&gt;]&gt;&lt;book&gt;&lt;title&gt;&amp;xxe;&lt;/title&gt;&lt;/book&gt;',
        bodyVariables: undefined
      },
      {
        type: 'dataflow',
        introTemplate:
          '{{#paragraph}}The XML parser would have resolved the following resources and placed them into the XML document:{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          '{{#taint}}file:///etc/passwd{{{nl}}}file:///etc/passwd{{{nl}}}{{/taint}}',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate:
          '{{#paragraph}}We did not block this attack because blocking was not enabled for {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/applications/965faeda-d021-4b60-81b4-9d436e7d6a0c$$LINK_DELIM$$AgentMessageGeneratorJava{{/link}} on {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/servers/2/overview$$LINK_DELIM$$FakeServer{{/link}}.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    requestChapters: [
      {
        type: 'request',
        body: 'POST /WebGoat/attack HTTP/1.0 {{{nl}}}User-Agent: Apache-HttpClient/4.5.2&nbsp;(Java/1.8.0_161) {{{nl}}}Accept-Encoding: gzip,deflate'
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector: ''
  },
  {
    id: '100ea0bf-976f-40a1-b5c4-cd0209ca22e4',
    ruleUuid: 'ip-blacklist',
    result: AttackStatus.BLOCKED,
    sourceIP: '0:0:0:0:0:0:0:1',
    received: '2019-04-04T14:23:54.000Z',
    displayName: 'IP: 0:0:0:0:0:0:0:1',
    type: 'IP',
    url: '/get-toggle',
    attackType: 'MANUAL',
    attackers: [],
    sourceName: undefined,
    tags: [],
    details: {
      findings: undefined,
      query: undefined,
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: undefined,
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: undefined,
    httpRequest: {
      method: 'GET',
      queryString: undefined,
      uri: '/get-toggle',
      version: '1.0',
      body: undefined,
      headers:
        '{"content-length":["26"],"accept-language":["en-US,en;q\\u003d0.9"],"cookie":["_ga\\u003dGA1.1.1072054443.1519245824; __utmz\\u003d111872281.1538493544.1.1.utmcsr\\u003d(direct)|utmccn\\u003d(direct)|utmcmd\\u003d(none); __utma\\u003d111872281.1072054443.1519245824.1551810215.1551819849.75; _gid\\u003dGA1.1.1803574245.1554125944; BAYEUX_BROWSER\\u003d2q9ro0s910q71k2e; JSESSIONID\\u003dA24D626415E341EEA40446366089D644"],"origin":["null"],"host":["localhost:8090"],"upgrade-insecure-requests":["1"],"User-Agent":["Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/73.0.3683.86 Safari/537.36"],"connection":["keep-alive"],"content-type":["application/x-www-form-urlencoded"],"cache-control":["max-age\\u003d0"],"accept-encoding":["gzip, deflate, br"],"accept":["text/html,application/xhtml+xml,application/xml;q\\u003d0.9,image/webp,image/apng,*/*;q\\u003d0.8,application/signed-exchange;v\\u003db3"]}',
      parameters: '{"funds":["100000"],"Change":["Toggle"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed a blacklisted IP address attempting to access a Protected Application. We blocked the user.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  },
  {
    id: '0c4b142b-258d-43a7-b2c1-053e9d760f25',
    ruleUuid: 'sql-injection',
    result: AttackStatus.EXPLOITED,
    sourceIP: '104.253.78.198',
    received: '2020-01-21T18:08:19.000Z',
    displayName: 'SQL Injection',
    type: 'PR',
    url: '/WebGoat/attack',
    attackType: 'MANUAL',
    attackers: ['guest'],
    sourceName: undefined,
    tags: [],
    details: {
      findings: ['TAUTOLOGIES'],
      query: 'SELECT * FROM FOO WHERE a=contrast-redacted-ssn AND 1=1',
      command: undefined,
      entryName: undefined,
      path: undefined
    },
    userInput: {
      value: 'SELECT * FROM FOO WHERE a=contrast-redacted-ssn AND 1=1',
      truncatedValue: false,
      name: undefined,
      type: 'UNKNOWN' as UserInputType
    },
    application: {
      id: '965faeda-d021-4b60-81b4-9d436e7d6a0c',
      name: 'AgentMessageGeneratorJava',
      role: 'ADMIN',
      language: AgentLanguage.JAVA.getName()
    },
    server: {
      id: '2',
      name: 'FakeServer',
      environment: 'DEVELOPMENT'
    },
    hidden: false,
    detectionType: 'SEMANTIC_ANALYSIS',
    httpRequest: {
      method: 'POST',
      queryString: 'Screen=contrast-redacted-address&menu=1200',
      uri: '/WebGoat/attack',
      version: '1.0',
      body: 'Username=contrast-redacted-account&amp;Password=%27+or+1%2B2%3D3+--&amp;SUBMIT=%27+or+112%3D112--',
      headers:
        '{"content-length":["82"],"cookie":["JSESSIONID\\u003d798C9EF64D586047B895658733214D40"],"host":["localhost:8080"],"User-Agent":["Apache-HttpClient/4.5.2 (Java/1.8.0_161)"],"content-type":["application/x-www-form-urlencoded; charset\\u003dUTF-8"],"connection":["Keep-Alive"],"accept-encoding":["gzip,deflate"]}',
      parameters:
        '{"Username":["contrast-redacted-account"],"Screen":["1135"],"SUBMIT":["\\u0027 or 112\\u003d112--"],"menu":["1200"],"Password":["\\u0027 or 1+2\\u003d3 --"]}',
      valid: true
    },
    storyChapters: [
      {
        type: 'source',
        introTemplate:
          '{{#paragraph}}We observed the following suspicious value enter the application through the Unknown: {{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          'POST /WebGoat/attack?Screen={{#redacted}}contrast-redacted-address{{/redacted}}&amp;menu=1200 HTTP/1.0\n{{{nl}}}\n{{{nl}}}\n\n{{{nl}}}{{{nl}}}Username={{#redacted}}contrast-redacted-account{{/redacted}}&amp;amp;Password=%27+or+1%2B2%3D3+--&amp;amp;SUBMIT=%27+or+112%3D112--',
        bodyVariables: undefined
      },
      {
        type: 'dataflow',
        introTemplate:
          '{{#paragraph}}This value was again observed altering the meaning of the SQL query executed within org.junit.jupiter.api.AssertThrows.assertThrows(AssertThrows.java:37):{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate:
          '{{#taint}}{{/taint}}SELECT&nbsp;*&nbsp;FROM&nbsp;FOO&nbsp;WHERE&nbsp;a={{#redacted}}contrast-redacted-ssn{{/redacted}}&nbsp;AND&nbsp;1=1',
        bodyVariables: undefined
      },
      {
        type: 'outcome',
        introTemplate:
          '{{#paragraph}}We did not block this attack because blocking was not enabled for {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/applications/965faeda-d021-4b60-81b4-9d436e7d6a0c$$LINK_DELIM$$AgentMessageGeneratorJava{{/link}} on {{#link}}http://localhost:19080/Contrast/static/ng/index.html#/3969ae2e-4453-45be-a0ba-af8db5e4528a/servers/2/overview$$LINK_DELIM$$FakeServer{{/link}}.{{/paragraph}}',
        introVariables: undefined,
        bodyTemplate: undefined,
        bodyVariables: undefined
      }
    ],
    method: 'executeQuery()',
    stackFrames: [
      {
        type: 'frameSink',
        description:
          'org.hsqldb.jdbc.jdbcStatement.executeQuery(Unknown Source)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.plugin.DOS_Login.createContent(DOS_Login.java:108)'
      },
      {
        type: 'frameCustom',
        description:
          'org.owasp.webgoat.lessons.AbstractLesson.handleRequest(AbstractLesson.java:868)'
      }
    ],
    file: 'Unknown File for class org.hsqldb.jdbc.jdbcStatement.java',
    lastCustomFrame: -1,
    signature: 'PIDS-SQLI-4001,PIDS-SQLI-42A,PIDS-SQLI-44C,TRUING_STRING_INJ',
    vector:
      'SELECT&nbsp;*&nbsp;FROM&nbsp;user_system_data&nbsp;WHERE&nbsp;user_name&nbsp;=&nbsp;&#39;{{#taint}}&#39;&nbsp;or&nbsp;112=112--{{/taint}}&#39;&nbsp;and&nbsp;password&nbsp;=&nbsp;&#39;&#39;&nbsp;or&nbsp;1+2=3&nbsp;--&#39;'
  }
]
